/**
 * MD-30 Phase C: Cross-platform analytics panel.
 *
 * Displays:
 *   - Per-platform engagement card grid (FB / IG / Threads / LinkedIn)
 *   - Top 5 cross-platform winners
 *
 * Reads from /api/v1/admin/social/insight (Phase C decoration with
 * `platform_breakdown` + `cross_platform_winners` fields).
 *
 * Per s2026-05-13-NO-DUPLICATE-CODE-RULE: this is a NEW panel; the
 * existing `SocialInsightPanel` (FB-focused KPI + brief) remains. The two
 * sit side-by-side in the Social tab — Analytics sub-tab vs Insight sub-tab.
 *
 * No external chart library — plain CSS bars/cards (per F5 PM audit:
 * `pages/package.json` has NO recharts/chart.js dep; do NOT add).
 */

import { useState } from 'react';
import { useAdminSocial } from './use-admin-social';
import type { InsightResponse } from '@core/social/types';

const PLATFORM_LABEL: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  threads: 'Threads',
  linkedin: 'LinkedIn',
};
const PLATFORM_COLOR: Record<string, string> = {
  facebook: '#1877f2',
  instagram: '#e1306c',
  threads: '#111827',
  linkedin: '#0a66c2',
};

function fmtNum(x: number | null | undefined): string {
  if (x == null) return '-';
  if (x >= 1000) return `${(x / 1000).toFixed(1)}k`;
  return String(Math.round(x));
}
function fmtPercent(x: number): string {
  return `${(x * 100).toFixed(1)}%`;
}
function fmtAge(ts: number): string {
  if (!ts) return '-';
  const ageSec = Math.max(0, Math.floor(Date.now() / 1000) - ts);
  if (ageSec < 3600) return `${Math.floor(ageSec / 60)} phut truoc`;
  if (ageSec < 86400) return `${Math.floor(ageSec / 3600)} gio truoc`;
  return `${Math.floor(ageSec / 86400)} ngay truoc`;
}

export function SocialAnalyticsPanel() {
  const [windowSel, setWindowSel] = useState<'7d' | '30d' | 'all'>('7d');
  const insight = useAdminSocial<InsightResponse>(`/insight?window=${windowSel}`);

  if (insight.loading) {
    return <p className="content-mgmt-loading">Dang tai du lieu phan tich cross-platform...</p>;
  }
  if (insight.error) {
    return <p className="content-mgmt-error">Loi tai du lieu: {insight.error}</p>;
  }
  if (!insight.data) {
    return <p className="content-mgmt-empty">Chua co du lieu.</p>;
  }

  const breakdown = insight.data.platform_breakdown ?? [];
  const winners = insight.data.cross_platform_winners ?? [];
  const hasData = breakdown.length > 0 || winners.length > 0;

  return (
    <section className="social-analytics-panel">
      <header className="social-analytics-header">
        <h3 className="social-analytics-title">Phan tich cross-platform</h3>
        <p className="social-analytics-sub">
          So sanh hieu qua giua Facebook, Instagram, Threads va LinkedIn. Du lieu cap nhat moi 24h
          (cron sync 04:00 ICT). Bai post chua co insight se khong xuat hien o day.
        </p>
        <label className="social-analytics-window">
          <span>Khoang thoi gian</span>
          <select value={windowSel} onChange={(e) => setWindowSel(e.target.value as typeof windowSel)}>
            <option value="7d">7 ngay gan nhat</option>
            <option value="30d">30 ngay gan nhat</option>
            <option value="all">Toan bo</option>
          </select>
        </label>
      </header>

      {!hasData && (
        <div className="social-analytics-empty">
          <p>
            Chua co du lieu insight cross-platform. Cron <code>social_insights_sync</code> chay luc
            04:00 ICT moi ngay sau khi co tai khoan ket noi va co bai post.
          </p>
          <p>
            Sau khi ket noi tai khoan IG / Threads / LinkedIn (qua tab Connect ben tren) va co bai
            post dau tien, du lieu se xuat hien trong vong 24 gio.
          </p>
        </div>
      )}

      {breakdown.length > 0 && (
        <div className="social-analytics-grid">
          {breakdown.map((p) => {
            const color = PLATFORM_COLOR[p.platform] ?? '#374151';
            const label = PLATFORM_LABEL[p.platform] ?? p.platform;
            return (
              <article
                key={p.platform}
                className="social-analytics-card"
                style={{ borderTopColor: color }}
              >
                <header className="social-analytics-card-header">
                  <span className="social-analytics-card-label" style={{ color }}>
                    {label}
                  </span>
                  <span className="social-analytics-card-count">{p.post_count} bai</span>
                </header>
                <dl className="social-analytics-card-stats">
                  <div>
                    <dt>Hien thi</dt>
                    <dd>{fmtNum(p.total_impressions)}</dd>
                  </div>
                  <div>
                    <dt>Tiep can</dt>
                    <dd>{fmtNum(p.total_reach)}</dd>
                  </div>
                  <div>
                    <dt>Like</dt>
                    <dd>{fmtNum(p.total_likes)}</dd>
                  </div>
                  <div>
                    <dt>Binh luan</dt>
                    <dd>{fmtNum(p.total_comments)}</dd>
                  </div>
                  <div>
                    <dt>Chia se</dt>
                    <dd>{fmtNum(p.total_shares)}</dd>
                  </div>
                  <div>
                    <dt>Click link</dt>
                    <dd>{fmtNum(p.total_link_clicks)}</dd>
                  </div>
                </dl>
                <footer className="social-analytics-card-footer">
                  <span className="social-analytics-card-er">
                    Tuong tac trung binh: <strong>{fmtPercent(p.avg_engagement_rate)}</strong>
                  </span>
                  <span className="social-analytics-card-sync">
                    Dong bo: {fmtAge(p.last_synced_at)}
                  </span>
                </footer>
              </article>
            );
          })}
        </div>
      )}

      {winners.length > 0 && (
        <div className="social-analytics-winners">
          <h4 className="social-analytics-winners-title">Top 5 bai post hieu qua nhat (cross-platform)</h4>
          <ol className="social-analytics-winners-list">
            {winners.map((w) => {
              const color = PLATFORM_COLOR[w.platform] ?? '#374151';
              const label = PLATFORM_LABEL[w.platform] ?? w.platform;
              return (
                <li key={w.social_post_id} className="social-analytics-winner-item">
                  <div className="social-analytics-winner-meta">
                    <span
                      className="social-analytics-winner-platform"
                      style={{ background: color }}
                    >
                      {label}
                    </span>
                    <span className="social-analytics-winner-er">
                      {fmtPercent(w.engagement_rate)} tuong tac
                    </span>
                    <span className="social-analytics-winner-age">{fmtAge(w.posted_at)}</span>
                  </div>
                  <p className="social-analytics-winner-caption">
                    {w.caption_snippet}
                    {w.caption_snippet.length >= 100 ? '...' : ''}
                  </p>
                  <div className="social-analytics-winner-stats">
                    <span>Hien thi: {fmtNum(w.impressions)}</span>
                    <span>Like: {fmtNum(w.likes)}</span>
                    <span>Binh luan: {fmtNum(w.comments)}</span>
                    {w.external_post_url && (
                      <a
                        href={w.external_post_url}
                        target="_blank"
                        rel="noreferrer"
                        className="social-analytics-winner-link"
                      >
                        Mo bai goc
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </section>
  );
}
